import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Flexbox,
  Heading,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  shootToast,
} from "codekit";

import { useGetCargosAdmpuestQuery } from "../../../libs/redux/slices/Usuarios/Admpuest/admpuestApiSlice";
import { useLazyGetMenuCargoQuery } from "../../../libs/redux/slices/Usuarios/Vercomo/vercomoApiSlice";
import {
  clearCargoPreview,
  getCargoPreview,
  getIsAdmin,
  getVercomoAbierto,
  setCargoPreview,
  setVercomoAbierto,
} from "../../../libs/redux/slices/authSlice/authSlice";
import { apiSlice } from "../../../libs/redux/slices/apiSlice";
import { Avisoxxx } from "../../Tablerox";

/**
 * "Ver como cargo" (Diego 2026-09-14): la ventana de elección de cargo y la
 * franja del modo, siempre montadas en la página principal. Al elegir un
 * cargo, el menú pasa a ser la plantilla de permisos de ese cargo y toda
 * petición viaja con la cabecera Cargoview: el backend responde con las
 * plantillas de Mi Puesto, del escritorio y del Inicio del cargo, en solo
 * lectura. "Volver a mi vista" restaura el menú propio.
 * ACTUAR como cargo (Diego 2026-09-22): el mismo administrador puede ACTUAR
 * como el cargo (cabecera Cargoactua): guarda de verdad y los permisos son
 * los de la plantilla del cargo. Sirve para probar cada puesto (Ejecutivo,
 * Especialista, Asistente...) alternando entre ellos; los últimos cargos
 * usados quedan como accesos rápidos.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-09-14
 * @param {function} handleClickHome Vuelve al Inicio (limpia módulo y proceso)
 */
const Vercomob = ({ handleClickHome }) => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(getIsAdmin);
  const cargoPreview = useSelector(getCargoPreview);
  const isOpen = useSelector(getVercomoAbierto);
  const [cargo, setCargo] = useState("");
  const [cargoLabel, setCargoLabel] = useState("");
  const [resultados, setResultados] = useState([]);
  //Cargos recientes de este navegador: accesos rápidos para alternar (Ejecutivo ↔ Especialista)
  const leerRecientes = () => {
    try { return JSON.parse(window.localStorage.getItem("vercomoRecientes") || "[]"); } catch (e) { return []; }
  };
  const [recientes, setRecientes] = useState(leerRecientes);
  const guardarReciente = (item) => {
    const lista = [item, ...leerRecientes().filter((r) => String(r.CARIDXXX) !== String(item.CARIDXXX))].slice(0, 6);
    try { window.localStorage.setItem("vercomoRecientes", JSON.stringify(lista)); } catch (e) { /* sin almacenamiento */ }
    setRecientes(lista);
  };
  const { data: cargosData } = useGetCargosAdmpuestQuery(undefined, { skip: !isAdmin });
  const [getMenuCargo, { isLoading }] = useLazyGetMenuCargoQuery();

  //Al cargar la app, el cargo actuado se refresca contra la parametría (Diego 2026-09-23: el 401 se renombró y la
  //franja seguía con el nombre viejo guardado en redux): nombre y menú al día; si el cargo quedó inactivo, se sale.
  useEffect(() => {
    if (!isAdmin || !cargoPreview?.CARIDXXX) {
      return;
    }
    getMenuCargo(String(cargoPreview.CARIDXXX))
      .unwrap()
      .then((resp) => {
        if (resp.data.CARDESXX !== cargoPreview.CARDESXX) {
          dispatch(setCargoPreview({ ...resp.data, ACTUAR: Boolean(cargoPreview.ACTUAR) }));
        }
      })
      .catch((error) => {
        if (error?.status === 404) {
          dispatch(clearCargoPreview());
          shootToast({ variant: "warning", text: `El cargo ${cargoPreview.CARIDXXX} ya no existe o está inactivo: volviste a tu cargo.`, duration: 6000, autoHide: true });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAdmin) {
    return null;
  }
  const cargos = (cargosData?.data ?? []).map((c) => ({
    value: String(c.CARIDXXX),
    label: `${c.CARIDXXX} - ${c.CARDESXX}`,
  }));
  const cerrar = () => dispatch(setVercomoAbierto(false));

  //Buscador local por número o por nombre del cargo (la lista ya está en memoria)
  const handleBuscar = (value, cb) => {
    const texto = String(value ?? "")
      .trim()
      .toUpperCase();
    setResultados(texto === "" ? cargos : cargos.filter((c) => c.label.toUpperCase().includes(texto)));
    cb();
  };

  //Todo el API vuelve a consultarse con la cabecera nueva (o sin ella)
  const reiniciar = () => {
    window.sessionStorage.removeItem("mipuestoAterrizado");
    dispatch(apiSlice.util.resetApiState());
    handleClickHome();
  };

  //actuar = true: ACTUAR como el cargo (guardar, permisos del cargo); false: solo ver
  const handleVer = async (actuar = false, cargoElegido = cargo) => {
    await getMenuCargo(cargoElegido)
      .unwrap()
      .then((resp) => {
        //Cargo sin plantilla de permisos: se entra igual (el administrador decide), pero avisando por qué no hay menú
        if (resp.data?.PLANTILLA === "NO") {
          shootToast({
            variant: "warning",
            text: `El cargo ${resp.data.CARIDXXX} · ${resp.data.CARDESXX} no tiene plantilla de permisos (${resp.data.USUARIOS ?? 0} usuario(s) con ese cargo): verás el sistema SIN menú. Si es un cargo huérfano, escoge el vigente; para importaciones el Especialista es el 401.`,
            duration: 9000,
            autoHide: true,
          });
        }
        dispatch(setCargoPreview({ ...resp.data, ACTUAR: actuar }));
        guardarReciente({ CARIDXXX: resp.data.CARIDXXX, CARDESXX: resp.data.CARDESXX });
        cerrar();
        reiniciar();
      })
      .catch((error) => {
        //Cargo inactivo o inexistente (404): sale de los recientes para que el cambio rápido no lo ofrezca más
        if (error?.status === 404) {
          const lista = leerRecientes().filter((r) => String(r.CARIDXXX) !== String(cargoElegido));
          try { window.localStorage.setItem("vercomoRecientes", JSON.stringify(lista)); } catch (e) { /* sin almacenamiento */ }
          setRecientes(lista);
        }
        shootToast({
          variant: "error",
          text: error?.data?.errors?.message ?? "No se pudo cargar el menú del cargo",
          duration: 4000,
          autoHide: true,
        });
      });
  };

  const handleVolver = () => {
    dispatch(clearCargoPreview());
    cerrar();
    reiniciar();
  };

  return (
    <>
      {cargoPreview && (
        <Box mb={3}>
          <Avisoxxx
            tonoxxx={cargoPreview.ACTUAR ? "NARANJA" : "MORADO"}
            kicker={cargoPreview.ACTUAR ? "Actuando como otro cargo · puedes guardar" : "Modo de mantenimiento · solo lectura"}
            valor={cargoPreview.CARIDXXX}
            titulo={`Estás ${cargoPreview.ACTUAR ? "ACTUANDO" : "viendo el sistema"} como ${cargoPreview.CARDESXX}`}
            texto={cargoPreview.ACTUAR
              ? "Menú, tableros y permisos son los de ese cargo y todo lo que guardes queda registrado con tu usuario. Úsalo para probar el puesto; vuelve a tu cargo al terminar."
              : "Menú, Mi Puesto, escritorio e Inicio con las plantillas de ese cargo. Tus datos y clientes siguen siendo los tuyos; solo puedes guardar en la parametría de puestos."}
            acciones={[
              { clave: "volver", texto: "Volver a mi cargo", primaria: true, onClick: handleVolver },
              { clave: "otro", texto: "Otro cargo", onClick: () => dispatch(setVercomoAbierto(true)) },
            ]}
          />
          {/* Cambio rápido de cargo (Diego 2026-09-23): para probar puestos hay que saltar de uno a otro en un clic,
              sin abrir la ventana. Chips con los cargos recientes y un selector con todos; conserva el modo (ver/actuar). */}
          <div className="rio-card" style={{ marginTop: 6, padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span className="rio-kicker">Cambio rápido · {cargoPreview.ACTUAR ? "actuar como" : "ver como"}</span>
            {recientes.filter((r) => String(r.CARIDXXX) !== String(cargoPreview.CARIDXXX)).map((r) => (
              <Button key={r.CARIDXXX} type="button" variant="secondary" size="s" isLoading={isLoading} onClick={() => handleVer(Boolean(cargoPreview.ACTUAR), String(r.CARIDXXX))}>
                {r.CARIDXXX} · {r.CARDESXX}
              </Button>
            ))}
            <div style={{ minWidth: 320 }}>
              <Select
                placeholder="Cualquier otro cargo…"
                value=""
                options={cargos.map((c) => ({ text: c.label, value: c.value }))}
                onChangeValue={(context) => {
                  const valor = String(context?.option?.value ?? "");
                  if (valor !== "" && valor !== String(cargoPreview.CARIDXXX)) {
                    handleVer(Boolean(cargoPreview.ACTUAR), valor);
                  }
                }}
              />
            </div>
          </div>
        </Box>
      )}
      {isOpen && (
        <Modal size="md" isOpen={isOpen}>
          <ModalHeader>
            <Flexbox justifyContent="between" alignItems="center">
              <Heading variant="heading-5" color="primary-700">
                Ver el sistema como un cargo
              </Heading>
              <Button variant="secondary" icon="close" onlyIcon size="m" onClick={cerrar} />
            </Flexbox>
          </ModalHeader>
          <ModalBody size="sm">
            <Label>Cargo</Label>
            <Autocomplete
              placeholder="Escribe el número o el nombre del cargo"
              textLoading="Buscando..."
              textNoResults="No hay cargos con ese nombre o número"
              minLengthToSearch={1}
              searchResults={resultados}
              selected={cargoLabel}
              onChangeSearch={handleBuscar}
              onItemClick={(context) => {
                setCargoLabel(context.item.label);
                setCargo(String(context.item.value));
              }}
              onResetSelected={() => {
                setCargoLabel("");
                setCargo("");
              }}
            />
            {recientes.length > 0 && (
              <Box mt={3}>
                <Label color="neutral-600" weight="bold">Recientes</Label>
                <Flexbox gap={2} style={{ flexWrap: "wrap", marginTop: 6 }}>
                  {recientes.map((r) => (
                    <Button key={r.CARIDXXX} type="button" variant="secondary" size="s" onClick={() => { setCargo(String(r.CARIDXXX)); setCargoLabel(`${r.CARIDXXX} - ${r.CARDESXX}`); }}>
                      {r.CARIDXXX} · {r.CARDESXX}
                    </Button>
                  ))}
                </Flexbox>
              </Box>
            )}
            <Box mt={3}>
              <Label color="neutral-500">
                <strong>Solo ver:</strong> menú, Mi Puesto, escritorio e Inicio con las plantillas del cargo, en solo lectura.
                <br />
                <strong>Actuar:</strong> además puedes guardar y los permisos son los de la plantilla del cargo; lo guardado queda con tu usuario.
              </Label>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Flexbox justifyContent="between" alignItems="center" gap={4}>
              {cargoPreview ? (
                <Button type="button" variant="secondary" size="2l" onClick={handleVolver}>
                  Volver a mi vista
                </Button>
              ) : (
                <span />
              )}
              <Flexbox gap={2}>
                <Button type="button" variant="secondary-bold" size="2l" isLoading={isLoading} disabled={!cargo} onClick={() => handleVer(false)}>
                  Solo ver
                </Button>
                <Button type="button" variant="primary-bold" size="2l" minWidth={160} isLoading={isLoading} disabled={!cargo} onClick={() => handleVer(true)}>
                  Actuar como este cargo
                </Button>
              </Flexbox>
            </Flexbox>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default Vercomob;
